/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 필드셋 클래스를 정의한다.
 *
 * @file /src/components/form/field/set/Set.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import * as parent from '@components/form/field/Field';
import { FieldDefaults } from '@components/form/Form';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { LanguageText } from '@utils/Language';
import './Set.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 필드셋이 축소될 때
     */
    collapse?: (me: T) => void;

    /**
     * 필드셋이 확장될 때
     */
    expand?: (me: T) => void;

    /**
     * 체크여부가 변경될 때
     */
    change?: (me: T, checked: boolean) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 도움말
     */
    helpText?: LanguageText;

    /**
     * 체크가능여부
     */
    checkable?: boolean;

    /**
     * 체크여부
     */
    checked?: boolean;

    /**
     * 축소가능여부
     */
    collapsible?: boolean;

    /**
     * 축소여부
     */
    collapsed?: boolean;

    /**
     * 필드 기본값
     */
    fieldDefaults?: FieldDefaults;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Set extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'set';

    /**
     * 도움말
     */
    helpText: LanguageText | null = null;

    /**
     * 축소가능여부
     */
    checkable: boolean;

    /**
     * 축소여부
     */
    checked: boolean;

    /**
     * 축소가능여부
     */
    collapsible: boolean;

    /**
     * 축소여부
     */
    collapsed: boolean;

    /**
     * 필드 기본값
     */
    fieldDefaults: FieldDefaults;

    /**
     * 푸터 DOM
     */
    $footer: Dom = null;

    /**
     * 필드셋 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Set> = null) {
        super(properties);

        this.helpText = this.properties.helpText ?? null;
        this.checkable = this.properties.checkable === true;
        this.checked = this.properties.checked === true;
        this.collapsible = this.properties.collapsible === true;
        this.collapsed = this.properties.collapsed === true;

        if (this.checkable == true && this.collapsible == true) {
            this.collapsed = this.checked == false;
        }

        this.fieldDefaults = this.properties?.fieldDefaults ?? null;
    }

    /**
     * 라벨 DOM 을 가져온다.
     *
     * @returns 라벨 DOM
     */
    $getLabel(): Dom {
        if (this.$label === null) {
            this.$label = Html.create('legend');

            if (this.collapsible == true || this.checkable == true) {
                this.$label.on('click', ($label) => {
                    if ($label.getAttr('disabled') == 'disabled') {
                        return;
                    }

                    this.toggle();
                });
            }
        }

        return this.$label;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<object | boolean> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.inputName !== null) {
            if (this.checkable == true && this.isChecked() == false) {
                return false;
            }

            const values: { [key: string]: any } = {};
            for (const field of this.items) {
                if (field instanceof parent.Field) {
                    Object.assign(values, await field.getValues());
                }
            }

            return values;
        } else {
            return null;
        }
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: { [key: string]: any } | boolean, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        if (typeof value == 'boolean') {
            if (this.checkable == true) {
                this.onCheck(value);
            }

            if (this.collapsible == true) {
                if (value == true) {
                    this.expand();
                } else {
                    this.collapse();
                }
            }
        } else {
            if (this.checkable == true) {
                this.onCheck(true);
            }

            if (this.inputName !== null && this.getFields(true).length > 0) {
                const promises: Promise<void>[] = [];

                for (const name in value) {
                    promises.push(this.getField(name)?.setValue(value[name]));
                }

                await Promise.all(promises);
            }
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드셋 축소/확장 여부를 토글한다.
     */
    toggle(): void {
        if (this.collapsible == true) {
            if (this.isCollapsed() == true) {
                this.expand();
            } else {
                this.collapse();
            }
        } else if (this.checkable == true) {
            this.onCheck(this.isChecked() == false);
        }
    }

    /**
     * 필드셋을 확장한다.
     */
    expand(): void {
        if (this.isCollapsed() == false) {
            return;
        }

        if (this.checkable == true && this.isChecked() == false) {
            this.onCheck(true);
        }

        if (this.collapsible == true) {
            this.$getContainer().removeClass('collapsed');
            this.fireEvent('expand', [this]);
        }
    }

    /**
     * 필드셋을 축소한다.
     */
    collapse(): void {
        if (this.isCollapsed() == true) {
            return;
        }

        if (this.checkable == true && this.isChecked() == true) {
            this.onCheck(false);
        }

        if (this.collapsible == true) {
            this.$getContainer().addClass('collapsed');
            this.fireEvent('collapse', [this]);
        }
    }

    /**
     * 체크여부를 가져온다.
     *
     * @returns 체크여부
     */
    isChecked(): boolean {
        return this.checkable == true && this.$getContainer().hasClass('checked') == true;
    }

    /**
     * 축소여부를 가져온다.
     *
     * @returns 축소여부
     */
    isCollapsed(): boolean {
        if (this.collapsible == true) {
            return this.$getContainer().hasClass('collapsed') == true;
        } else if (this.checkable == true) {
            return this.isChecked() == false;
        }

        return false;
    }

    /**
     * 에러메시지를 표시한다.
     *
     * @param message - 에러메시지
     */
    setErrorMessage(message: string = null): void {
        if (this.inputName !== null && this.getFields(true).length == 0) {
            this.setHelpText(message ?? this.helpText, false);
        } else {
            this.$getContainer().removeClass('error');
            for (const field of this.getFields(true)) {
                const validation = field.getValidation();
                field.setErrorMessage(validation.message ?? null);
            }
        }
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        for (const item of this.items) {
            item.setDisabled(disabled);
        }

        if (disabled == true) {
            this.$getLabel().setAttr('disabled', 'disabled');
        } else {
            this.$getLabel().removeAttr('disabled');
        }

        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     */
    focus(): void {
        const fields = this.getFields();
        for (const field of fields) {
            if (field.$getComponent().getAttr('data-role') === 'grid') continue;
            if (field.isShow() == true && field.isDisabled() == false) {
                field.focus();
                return;
            }
        }

        super.focus();
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();

        if (this.fieldDefaults !== null) {
            const labelWidth = this.fieldDefaults.labelWidth ?? null;
            const labelLength = this.fieldDefaults.labelLength ?? null;
            if (labelWidth !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelWidth}px`);
            } else if (labelLength !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelLength}rem`);
            }

            const labelPosition = this.fieldDefaults.labelPosition ?? 'left';
            if (labelPosition != 'left') {
                this.$getComponent().setStyleProperty('--aui-form-label-width', '100%');
                this.$getComponent().setStyleProperty('--aui-form-columns', '1fr auto');
                this.$getComponent().setStyleProperty(
                    '--aui-form-areas',
                    `'label label' 'content buttons' 'helptext helptext'`
                );
            }

            const labelAlign = this.fieldDefaults.labelAlign ?? (labelPosition == 'top' ? 'left' : 'right');
            if (labelAlign != 'right') {
                this.$getComponent().setStyleProperty('--aui-form-label-align', labelAlign);
                this.$getComponent().setStyleProperty('--aui-form-label-padding', '0');
            }

            const labelSeparator = this.fieldDefaults.labelSeparator ?? (labelPosition == 'top' ? '' : ':');
            if (labelSeparator !== ':') {
                this.$getComponent().setStyleProperty('--aui-form-label-separator', `"${labelSeparator}"`);
            }
        }

        if (this.checkable == true) {
            this.$getContainer().addClass('checkable');
        }

        if (this.collapsible == true) {
            this.$getContainer().addClass('collapsible');

            if (this.collapsed == true) {
                this.$getContainer().addClass('collapsed');
            }
        }
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        await super.renderItems();
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.checkable == true) {
            if (this.checked == true) {
                this.onCheck(true, true);

                if (this.collapsible == true) {
                    this.expand();
                }
            } else {
                this.onCheck(false, true);

                if (this.collapsible == true) {
                    this.collapse();
                }
            }
        }

        await super.renderState();
    }

    /**
     * 필드셋 체크상태를 변경한다.
     *
     * @param checked - 체크여부
     * @param is_init - 초기값 여부
     */
    onCheck(checked: boolean, is_init: boolean = false): void {
        if (is_init == false && this.isChecked() == checked) {
            return;
        }

        if (checked == true) {
            this.$getContainer().addClass('checked');
        } else {
            this.$getContainer().removeClass('checked');
        }

        for (const item of this.items) {
            item.setDisabled(checked !== true);
        }

        if (is_init == false) {
            this.fireEvent('change', [this, checked]);
        }
    }
}
